
import React, { useState, useCallback, useRef } from 'react';
import { UploadCloud, FileVideo, Music, Lock } from 'lucide-react';

const FREE_MAX_BYTES = 100 * 1024 * 1024;  // 100 MB
const PAID_MAX_BYTES = 500 * 1024 * 1024; // 500 MB
const MAX_CONCURRENT_JOBS = 3;
const maxFiles = 10; // paid users can bulk upload up to 10 files

interface FileUploaderProps {
  onFileSelect: (file: File) => void;
  disabled?: boolean;
  isPaid?: boolean;
}

const acceptedFileTypes = "audio/*,video/*";

const FileUploader: React.FC<FileUploaderProps> = ({ onFileSelect, disabled, isPaid = false }) => {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const maxBytes = isPaid ? PAID_MAX_BYTES : FREE_MAX_BYTES;
  const maxLabel = isPaid ? '500 MB' : '100 MB';

  const handleFile = useCallback((files: FileList | null) => {
    if (!files || files.length === 0) return;

    // Free users attempting bulk upload — show upsell message
    if (!isPaid && files.length > 1) {
      alert('Bulk upload is a Lifetime feature. Upgrade to upload up to 10 files at once.');
      return;
    }

    const limit = isPaid ? maxFiles : 1;
    const accepted = Array.from(files).slice(0, limit);

    for (const file of accepted) {
      if (!file.type.startsWith('audio/') && !file.type.startsWith('video/')) {
        alert(`Unsupported file type: "${file.name}". Please upload audio or video files.`);
        continue;
      }
      if (file.size > maxBytes) {
        alert(`File too large: "${file.name}". ${isPaid ? 'Paid' : 'Free'} accounts support files up to ${maxLabel}. ${!isPaid ? 'Upgrade to Lifetime for up to 500 MB.' : ''}`);
        continue;
      }
      onFileSelect(file);
    }
  }, [onFileSelect, maxBytes, maxLabel, isPaid]);

  const handleDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (!disabled) setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    if (disabled) return;
    handleFile(e.dataTransfer.files);
  };
  
  const handleClick = () => {
      if(!disabled) fileInputRef.current?.click();
  }

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => handleFile(e.target.files);
  
  return (
    <div className="w-full">
         <div
            className={`group relative w-full p-10 md:p-14 text-center rounded-3xl transition-all duration-300 ease-out overflow-hidden cursor-pointer ${
                disabled ? 'opacity-90 bg-base-200 dark:bg-dark-300 cursor-not-allowed' : 'hover:bg-base-200/50 dark:hover:bg-dark-300/30'
            }`}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            onClick={handleClick}
            role="button"
            tabIndex={disabled ? -1 : 0}
        >
            {/* Dashed Border with Gradient support */}
            <div className={`absolute inset-0 rounded-3xl border-2 border-dashed transition-colors duration-300 ${isDragging ? 'border-brand-primary bg-brand-primary/5' : disabled ? 'border-transparent' : 'border-base-300 dark:border-dark-300 group-hover:border-brand-primary/50'}`} />
            
            <input
                ref={fileInputRef}
                type="file"
                className="hidden"
                accept={acceptedFileTypes}
                multiple={isPaid}
                onChange={handleInputChange}
                disabled={disabled}
            />
            
            {disabled ? (
                <div className="relative z-10 flex flex-col items-center justify-center space-y-4 pointer-events-none opacity-50">
                     <div className="w-20 h-20 rounded-full bg-base-300 dark:bg-dark-100 flex items-center justify-center mb-2">
                        <Lock className="w-8 h-8 text-base-content/50 dark:text-dark-content/50" />
                     </div>
                     <div>
                        <h3 className="text-xl md:text-2xl font-bold text-base-content dark:text-dark-content mb-2">
                            Upload Locked
                        </h3>
                        <p className="text-base-content/60 dark:text-dark-content/60 text-base max-w-sm mx-auto">
                            Queue full or credit limit reached.
                        </p>
                    </div>
                </div>
            ) : (
                <div className="relative z-10 flex flex-col items-center justify-center space-y-4 pointer-events-none">
                    <div className={`w-20 h-20 rounded-full bg-base-100 dark:bg-dark-300 shadow-xl shadow-base-300/50 dark:shadow-none flex items-center justify-center mb-2 transition-transform duration-300 ${isDragging ? 'scale-110 rotate-12' : 'group-hover:scale-105 group-hover:-translate-y-1'}`}>
                        {isDragging ? (
                            <UploadCloud className="w-10 h-10 text-brand-primary animate-bounce" />
                        ) : (
                            <UploadCloud className="w-10 h-10 text-base-content/80 dark:text-dark-content/80 group-hover:text-brand-primary transition-colors" />
                        )}
                    </div>
                    
                    <div>
                        <h3 className="text-xl md:text-2xl font-bold text-base-content dark:text-dark-content mb-2">
                            {isDragging ? "Drop it like it's hot!" : "Upload Media"}
                        </h3>
                        <p className="text-base-content/60 dark:text-dark-content/60 text-base max-w-sm mx-auto">
                            Drag & drop audio or video files here, or <span className="text-brand-primary font-medium underline decoration-2 decoration-brand-primary/30 underline-offset-2">browse</span>
                        </p>
                    </div>
                    
                    <div className="flex items-center gap-3 pt-2 text-xs font-medium text-base-content/40 dark:text-dark-content/40 uppercase tracking-wider">
                        <span className="flex items-center gap-1"><Music size={12}/> MP3, WAV</span>
                        <span className="w-1 h-1 rounded-full bg-base-300 dark:bg-dark-300" />
                        <span className="flex items-center gap-1"><FileVideo size={12}/> MP4, MOV</span>
                        <span className="w-1 h-1 rounded-full bg-base-300 dark:bg-dark-300" />
                        <span>Max {maxLabel}</span>
                    </div>
                </div>
            )}
        </div>
    </div>
  );
};

export default FileUploader;
